import { LoadingButton } from '@mui/lab';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import React, { useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { authHeader, handleResponse, queryClient } from '../queryClient';
import StudentsFormFields from '../students-form-fields/StudentsFormFields';
import { useActiveCompany } from '../utils/useCompany';

export interface ManageStudentsDialogProps {
  exerciseId: string;
  onClose: () => void;
  onConfirm: () => void;
  open: boolean;
  simulatorId: string;
  studentIds: string[];
}

interface StudentsFormData {
  users: string[];
}

const ManageStudentsDialog = ({
  exerciseId,
  onClose,
  onConfirm,
  open,
  simulatorId,
  studentIds,
}: ManageStudentsDialogProps) => {
  const { formState, handleSubmit, reset, setError, setValue, watch } = useForm<StudentsFormData>({
    defaultValues: { users: studentIds },
  });

  const activeCompany = useActiveCompany();

  const { isLoading, mutate: saveUsers } = useMutation<{ exerciseId: string }, Response, string[]>({
    async mutationFn(data) {
      return fetch(`/api/teach/companies/${activeCompany?.companyId}/scheduled-exercises/${exerciseId}/users`, {
        body: JSON.stringify(data),
        headers: { ...(await authHeader()), 'Content-Type': 'application/json' },
        method: 'PUT',
      }).then(handleResponse);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [{ path: `teach/companies/${activeCompany?.companyId}/scheduled-exercises/${exerciseId}/users` }],
      });

      onConfirm();
    },
    onError: async error => {
      const body = await error.json();

      setError('users', { message: body.title });
    },
  });

  const onSubmit: SubmitHandler<StudentsFormData> = (data, e) => {
    e?.preventDefault();
    saveUsers(data.users);
  };

  useEffect(() => {
    if (open) {
      reset({ users: studentIds });
    }
  }, [open]);

  return (
    <Dialog fullWidth open={open} onClose={onClose}>
      <DialogTitle>Add/remove students</DialogTitle>

      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <StudentsFormFields
            onUpdate={users => {
              setValue('users', users);
            }}
            value={watch('users')}
            simulatorId={simulatorId}
          />

          {formState.errors.users && <Alert severity="error">{formState.errors.users?.message}</Alert>}
        </DialogContent>

        <DialogActions sx={{ display: 'flex', gap: 1 }}>
          <Button color="inherit" onClick={onClose}>
            Cancel
          </Button>
          <Box sx={{ flex: '1 1 auto' }} />
          <LoadingButton loading={isLoading} type="submit" variant="contained">
            Save
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};
export default ManageStudentsDialog;
